import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Compass, MapPin, User, ArrowRight, X } from 'lucide-react';

const KEY = 'gt_tour_done';

const SLIDES = [
  {
    icon: Sparkles,
    title: 'Meet your AI travel concierge',
    body: 'Tap the lime AI button anytime — get instant city plans, food picks, and survival tips tailored to where you are.',
    accent: 'from-[#C4FF3D] to-[#9be635]',
  },
  {
    icon: Compass,
    title: 'Essentials for any city',
    body: 'Visa, plugs, scams, taxis, holidays — everything a traveler needs the moment they arrive, in one tap.',
    accent: 'from-[#F2A47A] to-[#e88a5b]',
  },
  {
    icon: MapPin,
    title: 'Explore like a local',
    body: 'Hidden gems, food, free things, nightlife — curated for your radius with live opening hours.',
    accent: 'from-[#DDEAD0] to-[#b7d9a5]',
  },
  {
    icon: User,
    title: 'Your travel passport',
    body: 'Visited cities, badges, trip diary, and a glowing passport map — all in your profile.',
    accent: 'from-[#15291C] to-[#0F1A13]',
    dark: true,
  },
];

export default function GuidedTour() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    try {
      const done = localStorage.getItem(KEY);
      if (!done) {
        // tiny delay so it doesn't compete with first paint
        const t = setTimeout(() => setOpen(true), 700);
        return () => clearTimeout(t);
      }
    } catch {}
  }, []);

  const finish = () => {
    try { localStorage.setItem(KEY, '1'); } catch {}
    setOpen(false);
  };
  const next = () => {
    if (step < SLIDES.length - 1) setStep(s => s + 1);
    else finish();
  };

  if (!open) return null;
  const s = SLIDES[step];
  const Icon = s.icon;

  return (
    <AnimatePresence>
      <motion.div
        key="tour-backdrop"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-[100] bg-[#0F1A13]/60 backdrop-blur-sm flex items-end sm:items-center justify-center px-4 pb-6"
        data-testid="guided-tour"
      >
        <motion.div
          key={`slide-${step}`}
          initial={{ opacity: 0, y: 30, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className={`relative w-full max-w-[420px] rounded-[28px] overflow-hidden shadow-[0_24px_60px_rgba(15,26,19,0.35)] ${s.dark ? 'bg-[#0F1A13] text-[#F2F0E4]' : 'bg-[#FBFAF2] text-[#0F1A13]'}`}
          data-testid={`tour-slide-${step}`}
        >
          <button
            type="button"
            onClick={finish}
            aria-label="Skip tour"
            data-testid="tour-skip"
            className={`absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center transition ${s.dark ? 'bg-white/10 hover:bg-white/15 text-[#F2F0E4]' : 'bg-[#0F1A13]/5 hover:bg-[#0F1A13]/10 text-[#0F1A13]'}`}
          >
            <X className="w-4 h-4" />
          </button>

          <div className="px-7 pt-9 pb-7">
            <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${s.accent} flex items-center justify-center shadow-[0_10px_28px_rgba(15,26,19,0.18)]`}>
              <Icon className={`w-6 h-6 ${s.dark ? 'text-[#0F1A13]' : 'text-[#15291C]'}`} />
            </div>

            <p className={`mt-6 text-[10.5px] uppercase tracking-[0.22em] font-bold ${s.dark ? 'text-[#C4FF3D]' : 'text-[#15291C]'}`}>
              {step + 1} / {SLIDES.length}
            </p>

            <h2 className="mt-2 font-display text-[28px] leading-[1.1] font-bold tracking-tight">
              {s.title}
            </h2>
            <p className={`mt-3 text-[15px] leading-relaxed ${s.dark ? 'text-[#DDEAD0]/85' : 'text-[#4A5A50]'}`}>
              {s.body}
            </p>

            {/* dots */}
            <div className="mt-7 flex items-center gap-1.5">
              {SLIDES.map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-300 ${i === step ? 'w-6' : 'w-1.5'} ${i === step ? (s.dark ? 'bg-[#C4FF3D]' : 'bg-[#15291C]') : (s.dark ? 'bg-[#F2F0E4]/25' : 'bg-[#0F1A13]/15')}`}
                />
              ))}
            </div>

            <div className="mt-6 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={finish}
                data-testid="tour-skip-bottom"
                className={`text-sm font-semibold ${s.dark ? 'text-[#DDEAD0]/80 hover:text-[#C4FF3D]' : 'text-[#4A5A50] hover:text-[#15291C]'}`}
              >
                Skip
              </button>
              <button
                type="button"
                onClick={next}
                data-testid={step === SLIDES.length - 1 ? 'tour-done' : 'tour-next'}
                className={`gt-btn-primary tap-bounce inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm ${s.dark ? '!bg-[#C4FF3D] !text-[#0F1A13]' : ''}`}
              >
                {step === SLIDES.length - 1 ? "Let's go" : 'Next'}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
